import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  isLocalHost,
  isOwnedCxHost,
  normalizeExternalUrl,
  tenantSlugFromHost,
} from "../../domain/domainRouting";
import { resolveTenantRouting } from "../../services/tenantRoutingService";
import type { TenantRoutingResolution } from "../../services/tenantRoutingService";

function EmptyLinkLanding({
  loading,
  tenant,
}: {
  loading: boolean;
  tenant: TenantRoutingResolution | null;
}) {
  const companyName = tenant?.companyName?.trim() || "";
  const providerLabel = companyName || "your service provider";

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "32px 18px",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef4f8 100%)",
        color: "#172033",
        fontFamily:
          'Gilroy, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <section
        aria-live="polite"
        style={{
          width: "min(100%, 520px)",
          border: "1px solid rgba(23, 32, 51, 0.12)",
          borderRadius: 8,
          background: "#ffffff",
          boxShadow: "0 18px 50px rgba(23, 32, 51, 0.12)",
          padding: "34px 30px",
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 8,
            display: "grid",
            placeItems: "center",
            background: "#e7f0f7",
            color: "#0f5f8a",
            fontSize: 22,
            fontWeight: 800,
            marginBottom: 24,
          }}
        >
          {companyName ? companyName.slice(0, 1).toUpperCase() : "L"}
        </div>
        <p
          style={{
            margin: "0 0 8px",
            color: "#64748b",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0,
          }}
        >
          {loading ? "Checking this link" : "Empty link"}
        </p>
        <h1
          style={{
            margin: 0,
            fontSize: "clamp(30px, 7vw, 44px)",
            lineHeight: 1.05,
            letterSpacing: 0,
            color: "#172033",
          }}
        >
          {loading ? "One moment." : "This link is empty."}
        </h1>
        <p
          style={{
            margin: "18px 0 0",
            color: "#475569",
            fontSize: 18,
            lineHeight: 1.6,
          }}
        >
          {loading
            ? "We are checking whether there is an active customer experience attached to this address."
            : `Nothing is attached to this address right now. If you were trying to open a review, payment, or service update, use the full link from your text or email.`}
        </p>
        {!loading ? (
          <p
            style={{
              margin: "22px 0 0",
              paddingTop: 20,
              borderTop: "1px solid rgba(23, 32, 51, 0.1)",
              color: "#64748b",
              fontSize: 15,
              lineHeight: 1.55,
            }}
          >
            If you need a new link, contact {providerLabel}.
          </p>
        ) : null}
      </section>
    </main>
  );
}

const RootDomainRedirect: React.FC = () => {
  const [fallbackToDashboard, setFallbackToDashboard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<TenantRoutingResolution | null>(null);

  useEffect(() => {
    const host = window.location.host;

    if (!isOwnedCxHost(host) && !isLocalHost(host)) {
      setFallbackToDashboard(true);
      return;
    }

    let cancelled = false;

    async function resolveTenant() {
      const tenantSlug = tenantSlugFromHost(host);

      if (!tenantSlug) {
        setLoading(false);
        return;
      }

      const resolution = await resolveTenantRouting(host, tenantSlug).catch(
        () => null,
      );
      if (cancelled) return;

      setTenant(resolution);
      const redirectUrl = normalizeExternalUrl(resolution?.websiteUrl ?? "");
      if (redirectUrl) {
        window.location.replace(redirectUrl);
        return;
      }

      setLoading(false);
    }

    void resolveTenant();

    return () => {
      cancelled = true;
    };
  }, []);

  if (fallbackToDashboard) {
    return <Navigate to="/ratings" replace />;
  }

  return <EmptyLinkLanding loading={loading} tenant={tenant} />;
};

export default RootDomainRedirect;
